import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Plotly from 'plotly.js-dist-min';
import { fetchDevices, fetchDeviceHealthLogs } from '../services/api';
import './ThermalAnalysis.css';

// Zone sensor mappings (no positional data needed for analysis)
const ZONES = [
  { id: 'z1',  label: '1 - Top Left',             sensors: ['ac','sdr0','sdr1'] },
  { id: 'z2',  label: '2 - Top Center',            sensors: ['cpu-1-4','mmw2','mmw3'] },
  { id: 'z3',  label: '3 - Top Right',             sensors: ['cpu-0-3','cpu-1-2','cpu-1-6','cpuss-2','mdmss-2','video'] },
  { id: 'z6',  label: '6 - Main Board Mid-Right',  sensors: ['cpu-0-1','cpu-1-0','cpu-1-1','cpu-1-3','cpu-1-5','cpu-1-8','cpuss-1','cpuss-3','ddr','gpuss-1','mdmss-3','mmw_pa2','nspss-0','nspss-1','pa','pm8350_tz','pmr735b_tz','xo-therm'] },
  { id: 'z9',  label: '9 - Right Main',            sensors: ['aoss-0','battery','battery_temp_c','camera-0','camera-1','cpu-0-0','cpu-0-2','cpu-1-7','cpuss-0','gpuss-0','mdmss-0','mdmss-1','pm8350c_tz','pm8450_tz','sdr-mmw-therm','usb-therm'] },
  { id: 'z10', label: '10 - Batt PCB Left',        sensors: ['aoss-1'] },
  { id: 'z17', label: '17 - Sub Board USB',        sensors: ['nspss-2','pmr735a_tz'] },
];

const ALL_SENSORS = ZONES.flatMap(z => z.sensors);

const SYSTEM_CHANNELS = [
  { key: 'headroom',        label: 'Headroom' },
  { key: 'thermal_status',  label: 'Thermal Status' },
  { key: 'battery_temp',    label: 'Battery Temp (°C)' },
  { key: 'system_temp',     label: 'System Temp (°C)' },
  { key: 'battery_level',   label: 'Battery Level (%)' },
  { key: 'charging_power',  label: 'Charging Power (W)' },
  { key: 'voltage',         label: 'Voltage (mV)' },
];

const PALETTE = [
  '#FF6B35','#2196F3','#F7931E','#E91E63','#9C27B0','#00BCD4',
  '#FFC107','#FF5722','#8BC34A','#673AB7','#FF9800','#3F51B5',
  '#009688','#F44336','#CDDC39','#607D8B','#795548','#00ACC1',
  '#D81B60','#6D4C41','#5E35B1','#00897B','#F7931E','#FDC830',
  '#E53935','#1E88E5','#43A047','#FB8C00','#8E24AA','#26A69A',
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function chId(deviceId, type, key) { return `${deviceId}:${type}:${key}`; }

function chLabel(deviceId, type, key, multi) {
  const pfx = multi ? `${deviceId} · ` : '';
  if (type === 'system') {
    const sc = SYSTEM_CHANNELS.find(c => c.key === key);
    return pfx + (sc ? sc.label : key);
  }
  if (type === 'zone') {
    const z = ZONES.find(zn => zn.id === key);
    return pfx + (z ? `Zone ${z.label}` : key);
  }
  return pfx + key;
}

function extractChannel(logs, type, key) {
  return logs.map(log => {
    const th = log.tempHealth;
    const dh = log.deviceHealth;
    if (type === 'system') {
      switch (key) {
        case 'headroom':        return th?.headroom ?? null;
        case 'thermal_status': return th?.thermal_status ?? null;
        case 'battery_temp':   return th?.battery_temp_c ?? null;
        case 'system_temp':    return dh?.temperatureCelsius ?? null;
        case 'battery_level':  return dh?.batteryLevel ?? null;
        case 'charging_power': return dh?.chargingPower != null ? dh.chargingPower / 1000 : null;
        case 'voltage':        return dh?.voltageMillivolts ?? null;
        default: return null;
      }
    }
    if (type === 'zone') {
      const zone = ZONES.find(z => z.id === key);
      if (!zone) return null;
      const vals = zone.sensors.map(s => th?.[s]).filter(v => v != null && v > 0);
      return vals.length ? Math.max(...vals) : null;
    }
    if (type === 'sensor') return th?.[key] ?? null;
    return null;
  });
}

function makeDefaults(deviceId) {
  return [
    { id: 'p1', label: 'Headroom', channels: [
      { id: chId(deviceId,'system','headroom'), datasetId: deviceId, type: 'system', key: 'headroom' },
    ]},
    { id: 'p2', label: 'Zone Temperatures', channels: ZONES.map(z => ({
      id: chId(deviceId,'zone',z.id), datasetId: deviceId, type: 'zone', key: z.id,
    }))},
    { id: 'p3', label: 'Battery & Charging', channels: [
      { id: chId(deviceId,'system','battery_temp'), datasetId: deviceId, type: 'system', key: 'battery_temp' },
      { id: chId(deviceId,'system','charging_power'), datasetId: deviceId, type: 'system', key: 'charging_power' },
    ]},
  ];
}

// ── Component ────────────────────────────────────────────────────────────────

const ThermalAnalysis = ({ primaryDeviceId, primaryThermalLogs, active }) => {
  const [datasets, setDatasets] = useState({});
  const [panels, setPanels] = useState([]);
  const [colorMap, setColorMap] = useState({});
  const [panelSeq, setPanelSeq] = useState(10);
  const colorSeqRef = useRef(0);
  const chartRef = useRef(null);
  const initRef = useRef(false);
  const wasActive = useRef(false);

  // Add-device form state
  const [deviceList, setDeviceList] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addDevId, setAddDevId] = useState('');
  const [addLimit, setAddLimit] = useState(1000);
  const [addLoading, setAddLoading] = useState(false);

  // Time axis mode: absolute timestamps vs relative seconds from each dataset's start
  const [relativeTime, setRelativeTime] = useState(false);

  // Sidebar collapse state
  const [datasetsOpen, setDatasetsOpen] = useState(true);
  const [collapsedPanels, setCollapsedPanels] = useState({});

  const multiDs = Object.keys(datasets).length > 1;

  // Assign next palette color
  const nextColor = useCallback(() => {
    const c = PALETTE[colorSeqRef.current % PALETTE.length];
    colorSeqRef.current++;
    return c;
  }, []);

  // ── Initialization ──

  useEffect(() => {
    if (!primaryDeviceId) return;
    setDatasets(prev => ({
      ...prev,
      [primaryDeviceId]: { deviceId: primaryDeviceId, thermalLogs: primaryThermalLogs || [], isPrimary: true },
    }));
  }, [primaryDeviceId, primaryThermalLogs]);

  useEffect(() => {
    if (initRef.current || !primaryDeviceId || !primaryThermalLogs?.length) return;
    initRef.current = true;
    const defs = makeDefaults(primaryDeviceId);
    const cm = {};
    defs.forEach(p => p.channels.forEach(ch => { cm[ch.id] = nextColor(); }));
    setColorMap(cm);
    setPanels(defs);
  }, [primaryDeviceId, primaryThermalLogs, nextColor]);

  useEffect(() => { fetchDevices().then(setDeviceList).catch(() => {}); }, []);

  // Resize Plotly when tab becomes visible
  useEffect(() => {
    if (active && !wasActive.current && chartRef.current) {
      setTimeout(() => { try { Plotly.Plots.resize(chartRef.current); } catch(e) {} }, 120);
    }
    wasActive.current = active;
  }, [active]);

  // ── Dataset management ──

  const addDataset = useCallback(async () => {
    if (!addDevId || datasets[addDevId]) return;
    setAddLoading(true);
    try {
      const logs = await fetchDeviceHealthLogs(addDevId, addLimit);
      const tLogs = logs.filter(l => l.tempHealth && typeof l.tempHealth === 'object');
      setDatasets(prev => ({ ...prev, [addDevId]: { deviceId: addDevId, thermalLogs: tLogs, isPrimary: false } }));
      setShowAddForm(false);
      setAddDevId('');
    } catch (err) {
      console.error('Failed to load device:', err);
    } finally {
      setAddLoading(false);
    }
  }, [addDevId, addLimit, datasets]);

  const removeDataset = useCallback((devId) => {
    setDatasets(prev => { const n = { ...prev }; delete n[devId]; return n; });
    setPanels(prev => prev.map(p => ({ ...p, channels: p.channels.filter(ch => ch.datasetId !== devId) })));
  }, []);

  // ── Panel management ──

  const addPanel = useCallback(() => {
    setPanelSeq(s => s + 1);
    setPanels(prev => [...prev, { id: `p${panelSeq}`, label: `Panel ${prev.length + 1}`, channels: [] }]);
  }, [panelSeq]);

  const removePanel = useCallback((pid) => { setPanels(prev => prev.filter(p => p.id !== pid)); }, []);

  const renamePanel = useCallback((pid, label) => {
    setPanels(prev => prev.map(p => p.id === pid ? { ...p, label } : p));
  }, []);

  const movePanel = useCallback((pid, dir) => {
    setPanels(prev => {
      const i = prev.findIndex(p => p.id === pid);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const a = [...prev]; [a[i], a[j]] = [a[j], a[i]]; return a;
    });
  }, []);

  const resetPanels = useCallback(() => {
    if (!primaryDeviceId) return;
    const defs = makeDefaults(primaryDeviceId);
    colorSeqRef.current = 0;
    const cm = {};
    defs.forEach(p => p.channels.forEach(ch => { cm[ch.id] = nextColor(); }));
    setColorMap(cm);
    setPanels(defs);
  }, [primaryDeviceId, nextColor]);

  // ── Channel management ──

  const addChannel = useCallback((pid, spec) => {
    if (!spec) return;
    const [dId, typ, ...rest] = spec.split(':');
    const key = rest.join(':');
    const id = spec;
    setPanels(prev => prev.map(p => {
      if (p.id !== pid || p.channels.some(c => c.id === id)) return p;
      return { ...p, channels: [...p.channels, { id, datasetId: dId, type: typ, key }] };
    }));
    setColorMap(prev => prev[id] ? prev : { ...prev, [id]: nextColor() });
  }, [nextColor]);

  const removeChannel = useCallback((pid, cid) => {
    setPanels(prev => prev.map(p => p.id !== pid ? p : { ...p, channels: p.channels.filter(c => c.id !== cid) }));
  }, []);

  // ── Channel dropdown options ──

  const channelOptions = useMemo(() => {
    const groups = [];
    Object.keys(datasets).forEach(dId => {
      const pfx = multiDs ? `${dId} · ` : '';
      groups.push({ label: `${pfx}System`, items: SYSTEM_CHANNELS.map(sc => ({ value: chId(dId,'system',sc.key), label: sc.label })) });
      groups.push({ label: `${pfx}Zones`, items: ZONES.map(z => ({ value: chId(dId,'zone',z.id), label: `Zone ${z.label}` })) });
      groups.push({ label: `${pfx}Sensors`, items: ALL_SENSORS.map(s => ({ value: chId(dId,'sensor',s), label: s })) });
    });
    return groups;
  }, [datasets, multiDs]);

  // ── Build & render Plotly chart ──

  useEffect(() => {
    const el = chartRef.current;
    if (!el || !active) return;
    if (panels.length === 0) { try { Plotly.purge(el); } catch(e) {} return; }

    const traces = [];
    const n = panels.length;
    const gap = 0.025;
    const ph = (1 - gap * (n - 1)) / n;

    const isDark = document.documentElement.getAttribute('data-theme') !== 'light';
    const layout = {
      template: isDark ? 'plotly_dark' : 'plotly_white',
      plot_bgcolor: isDark ? '#1a1d20' : '#ffffff',
      paper_bgcolor: isDark ? '#1a1d20' : '#f6f8fa',
      font: { color: isDark ? '#999' : '#656d76', family: "'Inter','Segoe UI',Arial,sans-serif", size: 10 },
      hovermode: 'x unified',
      showlegend: false,
      autosize: true,
      margin: { l: 60, r: 15, t: 8, b: 36 },
    };

    panels.forEach((panel, pi) => {
      const top = 1 - pi * (ph + gap);
      const bot = top - ph;
      const num = pi + 1;
      const yRef = pi === 0 ? 'y' : `y${num}`;
      const xRef = pi === 0 ? 'x' : `x${num}`;
      const yKey = pi === 0 ? 'yaxis' : `yaxis${num}`;
      const xKey = pi === 0 ? 'xaxis' : `xaxis${num}`;

      panel.channels.forEach(ch => {
        const ds = datasets[ch.datasetId];
        if (!ds || !ds.thermalLogs.length) return;
        let xs;
        if (relativeTime) {
          const t0 = new Date(ds.thermalLogs[0].timestamp).getTime();
          xs = ds.thermalLogs.map(l => (new Date(l.timestamp).getTime() - t0) / 1000);
        } else {
          xs = ds.thermalLogs.map(l => new Date(l.timestamp));
        }
        const vals = extractChannel(ds.thermalLogs, ch.type, ch.key);
        const lbl = chLabel(ch.datasetId, ch.type, ch.key, multiDs);
        traces.push({
          x: xs, y: vals, type: 'scatter', mode: 'lines',
          line: { color: colorMap[ch.id] || '#888', width: 1.5 },
          name: lbl, yaxis: yRef, xaxis: xRef,
          hovertemplate: relativeTime ? `${lbl}: %{y:.2f} @ %{x:.0f}s<extra></extra>` : `${lbl}: %{y:.2f}<extra></extra>`,
        });
      });

      layout[yKey] = {
        title: { text: panel.label, font: { color: '#777', size: 10 }, standoff: 6 },
        domain: [Math.max(0, bot), top],
        tickfont: { color: '#888', size: 9 },
        gridcolor: '#292c30',
        showgrid: true,
        zeroline: false,
        autorange: true,
      };

      layout[xKey] = {
        anchor: yRef,
        ...(pi > 0 ? { matches: 'x' } : {}),
        tickfont: { color: '#888', size: 9 },
        gridcolor: '#292c30',
        showgrid: true,
        showticklabels: pi === n - 1,
        zeroline: false,
        ...(relativeTime && pi === n - 1 ? { title: { text: 'Elapsed (s)', font: { color: '#666', size: 9 } } } : {}),
      };
    });

    const config = {
      responsive: true,
      displayModeBar: true,
      displaylogo: false,
      modeBarButtonsToRemove: ['lasso2d', 'select2d'],
      scrollZoom: true,
    };

    Plotly.react(el, traces, layout, config);
  }, [panels, datasets, colorMap, multiDs, active, relativeTime]);

  // Cleanup
  useEffect(() => {
    const el = chartRef.current;
    return () => { if (el) try { Plotly.purge(el); } catch(e) {} };
  }, []);

  // Window resize
  useEffect(() => {
    const h = () => { if (chartRef.current && active) try { Plotly.Plots.resize(chartRef.current); } catch(e) {} };
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [active]);

  const togglePanelCollapse = useCallback((pid) => {
    setCollapsedPanels(prev => ({ ...prev, [pid]: !prev[pid] }));
  }, []);

  // ── Render ──

  return (
    <div className="thermal-analysis" style={{ display: active ? 'flex' : 'none' }}>
      {/* ──── Sidebar ──── */}
      <div className="ta-sidebar">

        {/* Datasets */}
        <div className="ta-section">
          <div className="ta-section-hdr" onClick={() => setDatasetsOpen(p => !p)}>
            <span className="ta-section-title">Datasets</span>
            <span className="ta-section-arrow">{datasetsOpen ? '▾' : '▸'}</span>
          </div>
          {datasetsOpen && (
            <div className="ta-section-body">
              {Object.values(datasets).map(ds => (
                <div key={ds.deviceId} className="ta-ds-row">
                  <span className="ta-ds-badge" style={{ background: ds.isPrimary ? '#fc5f21' : '#2196F3' }}>
                    {ds.deviceId}
                  </span>
                  <span className="ta-ds-count">{ds.thermalLogs.length} logs</span>
                  {!ds.isPrimary && (
                    <button className="ta-x-btn" onClick={() => removeDataset(ds.deviceId)} title="Remove dataset">×</button>
                  )}
                </div>
              ))}
              {!showAddForm ? (
                <button className="ta-text-btn" onClick={() => setShowAddForm(true)}>+ Add Device</button>
              ) : (
                <div className="ta-add-form">
                  <select value={addDevId} onChange={e => setAddDevId(e.target.value)} className="ta-select">
                    <option value="">Select device...</option>
                    {deviceList.filter(d => !datasets[d.deviceId]).map(d => (
                      <option key={d.deviceId} value={d.deviceId}>{d.deviceId}</option>
                    ))}
                  </select>
                  <div className="ta-add-row">
                    <label className="ta-add-label">Limit</label>
                    <input type="number" value={addLimit} onChange={e => setAddLimit(parseInt(e.target.value) || 500)}
                      className="ta-input" min={10} max={5000} step={100} />
                  </div>
                  <div className="ta-add-actions">
                    <button className="ta-btn ta-btn-primary" onClick={addDataset} disabled={!addDevId || addLoading}>
                      {addLoading ? 'Loading...' : 'Load'}
                    </button>
                    <button className="ta-btn" onClick={() => { setShowAddForm(false); setAddDevId(''); }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Panels */}
        <div className="ta-section ta-panels-section">
          <div className="ta-section-hdr">
            <span className="ta-section-title">Panels</span>
            <button className="ta-text-btn ta-text-btn-sm" onClick={resetPanels} title="Reset to defaults">Reset</button>
          </div>
          <div className="ta-panels-list">
            {panels.map((panel, idx) => {
              const collapsed = collapsedPanels[panel.id];
              return (
                <div key={panel.id} className="ta-panel-block">
                  <div className="ta-panel-hdr">
                    <button className="ta-collapse-btn" onClick={() => togglePanelCollapse(panel.id)}>
                      {collapsed ? '▸' : '▾'}
                    </button>
                    <input className="ta-panel-name" value={panel.label}
                      onChange={e => renamePanel(panel.id, e.target.value)} />
                    <div className="ta-panel-btns">
                      <button className="ta-icon-btn" onClick={() => movePanel(panel.id, -1)} disabled={idx === 0} title="Up">↑</button>
                      <button className="ta-icon-btn" onClick={() => movePanel(panel.id, 1)} disabled={idx === panels.length - 1} title="Down">↓</button>
                      <button className="ta-icon-btn ta-icon-btn-danger" onClick={() => removePanel(panel.id)} title="Remove">×</button>
                    </div>
                  </div>
                  {!collapsed && (
                    <div className="ta-panel-body">
                      <div className="ta-chips">
                        {panel.channels.map(ch => (
                          <span key={ch.id} className="ta-chip">
                            <span className="ta-chip-dot" style={{ background: colorMap[ch.id] || '#888' }} />
                            <span className="ta-chip-label">{chLabel(ch.datasetId, ch.type, ch.key, multiDs)}</span>
                            <button className="ta-chip-x" onClick={() => removeChannel(panel.id, ch.id)}>×</button>
                          </span>
                        ))}
                      </div>
                      <select className="ta-add-ch-select" value=""
                        onChange={e => { if (e.target.value) addChannel(panel.id, e.target.value); }}>
                        <option value="">+ Add channel...</option>
                        {channelOptions.map(g => (
                          <optgroup key={g.label} label={g.label}>
                            {g.items.map(it => (
                              <option key={it.value} value={it.value}>{it.label}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              );
            })}
            <button className="ta-text-btn" onClick={addPanel}>+ Add Panel</button>
          </div>
        </div>
      </div>

      {/* ──── Chart Area ──── */}
      <div className="ta-main">
        <div className="ta-chart-toolbar">
          <button
            className={`ta-btn ta-btn-sm ${relativeTime ? 'ta-btn-active' : ''}`}
            onClick={() => setRelativeTime(r => !r)}
            title={relativeTime ? 'Switch to absolute timestamps' : 'Switch to relative time (seconds from each dataset start — useful for comparing devices from different days)'}
          >
            {relativeTime ? 'Rel t (s)' : 'Abs time'}
          </button>
        </div>
        {panels.length === 0 ? (
          <div className="ta-empty">Add a panel from the sidebar to begin analysis.</div>
        ) : (
          <div ref={chartRef} className="ta-chart" />
        )}
      </div>
    </div>
  );
};

export default ThermalAnalysis;
